/**
 * Copy text to the system clipboard, with a legacy fallback.
 *
 * `navigator.clipboard` is only exposed in SECURE contexts (https:// or
 * localhost). Internal dev/self-hosted hosts like `dax-sisko:4200` are plain
 * http, where `navigator.clipboard` is `undefined` (or `writeText` rejects) —
 * the live pacing copy surfaced this as "Failed to copy pacing data" (Ray).
 * The hidden-textarea + `document.execCommand('copy')` fallback works in
 * http contexts and is still supported by Chrome; every failure path returns
 * false so callers can toast a clean failure.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to the legacy path
    }
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    try {
      textarea.select();
      textarea.setSelectionRange(0, text.length);
      return document.execCommand('copy');
    } finally {
      textarea.remove();
    }
  } catch {
    return false;
  }
}